import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Download, ImageIcon, Link2, Loader2, Save, Upload, X } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { getNetlifyFunctionsRoot } from '@/lib/utils';
import { useEventDetails } from './EventDetailsContext';

const MemoQRCodeCanvas = React.memo(QRCodeCanvas);
const MAX_METADATA_IMAGE_BYTES = 1 * 1024 * 1024;

type MetadataActionsProps = {
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
  saveLabel?: string;
};

function MetadataActions({
  saving,
  onCancel,
  onSave,
  saveLabel = 'Guardar',
}: MetadataActionsProps) {
  return (
    <div className="flex items-center justify-end gap-3 pt-3">
      <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
        Cancelar
      </Button>
      <Button type="button" onClick={onSave} disabled={saving}>
        {saving ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Guardando…
          </>
        ) : (
          <>
            <Save className="mr-2 h-4 w-4" />
            {saveLabel}
          </>
        )}
      </Button>
    </div>
  );
}

function buildUploadFileName(prefix: string, file: File) {
  const mapTypeToExt = (type: string) => {
    if (!type) return '';
    if (type.includes('jpeg')) return '.jpg';
    if (type.includes('jpg')) return '.jpg';
    if (type.includes('png')) return '.png';
    if (type.includes('webp')) return '.webp';
    if (type.includes('gif')) return '.gif';
    if (type.includes('svg')) return '.svg';
    return '';
  };

  const existingName = file.name || '';
  const sanitizedName = existingName.replace(/[^a-zA-Z0-9._-]/g, '_');
  const hasExt = /\.[a-zA-Z0-9]+$/.test(sanitizedName);
  const lowerName = sanitizedName.toLowerCase();
  const extension = hasExt
    ? lowerName.substring(lowerName.lastIndexOf('.'))
    : mapTypeToExt(file.type || '') || '.png';

  return `${prefix}_${Date.now()}${extension}`;
}

const ShareTab: React.FC = () => {
  const {
    eventId,
    albumUrl,
    qrUrl,
    qrRef,
    copyAlbumUrl,
    handleDownloadQr,
    settingsValues,
    updateSetting,
  } = useEventDetails();
  const { toast } = useToast();

  const savedMetaTitle = settingsValues.meta_title || '';
  const savedMetaDescription = settingsValues.meta_description || '';
  const savedMetaImage = settingsValues.meta_image || '';

  const [metaTitleDraft, setMetaTitleDraft] = useState(savedMetaTitle);
  const [metaDescriptionDraft, setMetaDescriptionDraft] = useState(savedMetaDescription);
  const [savingTextKey, setSavingTextKey] = useState<'meta_title' | 'meta_description' | null>(null);

  const ogImageInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedMetaImageFile, setSelectedMetaImageFile] = useState<File | null>(null);
  const [metaImagePreviewUrl, setMetaImagePreviewUrl] = useState<string | null>(null);
  const [uploadingMetaImage, setUploadingMetaImage] = useState(false);

  useEffect(() => {
    setMetaTitleDraft(savedMetaTitle);
  }, [savedMetaTitle]);

  useEffect(() => {
    setMetaDescriptionDraft(savedMetaDescription);
  }, [savedMetaDescription]);

  useEffect(() => {
    return () => {
      if (metaImagePreviewUrl) {
        URL.revokeObjectURL(metaImagePreviewUrl);
      }
    };
  }, [metaImagePreviewUrl]);

  const isTitleDirty = metaTitleDraft !== savedMetaTitle;
  const isDescriptionDirty = metaDescriptionDraft !== savedMetaDescription;

  const activeMetaImagePreview = metaImagePreviewUrl || savedMetaImage;

  const setMetaImagePreview = (nextUrl: string) => {
    if (metaImagePreviewUrl) {
      URL.revokeObjectURL(metaImagePreviewUrl);
    }
    setMetaImagePreviewUrl(nextUrl);
  };

  const clearSelectedMetaImage = () => {
    setSelectedMetaImageFile(null);
    if (metaImagePreviewUrl) {
      URL.revokeObjectURL(metaImagePreviewUrl);
    }
    setMetaImagePreviewUrl(null);
  };

  const handleMetaImageSelect = (file: File | null) => {
    if (!file) return;

    if (file.size > MAX_METADATA_IMAGE_BYTES) {
      toast({
        title: 'Archivo demasiado pesado',
        description: 'El archivo supera el tamaño máximo de 1 MB.',
        variant: 'destructive',
      });
      return;
    }

    if ((file.type || '').toLowerCase() && !(file.type || '').toLowerCase().startsWith('image/')) {
      toast({
        title: 'Formato no soportado',
        description: 'Seleccioná un archivo de imagen válido.',
        variant: 'destructive',
      });
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setMetaImagePreview(previewUrl);
    setSelectedMetaImageFile(file);
  };

  const uploadMetaImage = async () => {
    if (!eventId || !selectedMetaImageFile) {
      return;
    }

    setUploadingMetaImage(true);

    try {
      const fileName = buildUploadFileName('meta_image', selectedMetaImageFile);
      const response = await fetch(
        `${getNetlifyFunctionsRoot()}/upload-to-cdn?fileName=${encodeURIComponent(fileName)}&albumId=${encodeURIComponent(eventId)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/octet-stream',
          },
          body: selectedMetaImageFile,
        },
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.url) {
        throw new Error((data && (data.body || data.error || data.message)) || 'No pudimos subir la imagen.');
      }

      const saved = await updateSetting('meta_image', data.url);
      if (!saved) {
        throw new Error('No pudimos guardar la URL del recurso.');
      }

      clearSelectedMetaImage();
      toast({
        title: 'Imagen OG actualizada',
        description: 'El recurso se guardó correctamente.',
      });
    } catch (error) {
      console.error('Metadata upload error', error);
      toast({
        title: 'Error al subir',
        description: 'No pudimos subir la imagen. Intentá nuevamente.',
        variant: 'destructive',
      });
    } finally {
      setUploadingMetaImage(false);
    }
  };

  const saveTextField = async (key: 'meta_title' | 'meta_description', value: string) => {
    setSavingTextKey(key);

    try {
      const saved = await updateSetting(key, value);
      if (!saved) {
        toast({
          title: 'Error al guardar',
          description: 'No pudimos guardar este campo. Intentá nuevamente.',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Metadatos actualizados',
        description: 'El campo se guardó correctamente.',
      });
    } finally {
      setSavingTextKey(null);
    }
  };

  const ogImagePreviewLabel = useMemo(() => {
    if (selectedMetaImageFile) return selectedMetaImageFile.name;
    if (savedMetaImage) return 'Imagen configurada';
    return 'Todavía no hay una imagen OG configurada.';
  }, [savedMetaImage, selectedMetaImageFile]);

  return (
    <div className="panel-card panel-card--main p-6">
      <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)] xl:items-start">
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Código QR</h2>
            <p className="text-sm text-slate-500">Descargá el código QR del evento</p>
          </div>

          <div className="group relative flex min-h-[240px] items-center justify-center rounded-2xl border border-indigo-100 bg-white/80 px-6 py-6 text-center shadow-inner">
            {qrUrl ? (
              <div ref={qrRef} className="relative">
                <MemoQRCodeCanvas value={qrUrl} size={180} includeMargin={true} level="M" bgColor="rgba(0,0,0,0)" />
                <button
                  type="button"
                  onClick={handleDownloadQr}
                  className="absolute inset-0 flex items-center justify-center rounded bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Descargar código QR"
                  title="Descargar QR"
                >
                  <Download className="h-8 w-8 text-white" />
                </button>
              </div>
            ) : (
              <div className="text-sm text-slate-400">Sin enlace disponible</div>
            )}
          </div>
        </section>

        <section className="space-y-6">
          <div className="space-y-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Link del Evento</h2>
              <p className="text-sm text-slate-500">Enlace público del evento:</p>
            </div>

            <div className="rounded-2xl border border-indigo-100 bg-white/90 p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 break-all text-sm text-slate-600">
                  {albumUrl ? `app.bloomkeep.site${albumUrl}` : 'Sin enlace disponible'}
                </span>
                {albumUrl && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => { void copyAlbumUrl(); }}
                    className="h-9 w-9 text-slate-500 hover:text-slate-700 hover:bg-purple-200"
                    aria-label="Copiar enlace del evento"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="text-md font-semibold text-slate-800">Vista Previa del Enlace</h3>
            </div>
            <p className="text-sm text-slate-500">
              La siguiente información se ve cuando el enlace se comparte por WhatsApp, e-mail, o redes sociales:
            </p>

          </div>

          <div className="space-y-2">
            <label htmlFor="meta-title-input" className="text-sm font-semibold text-slate-700">
              Título
            </label>
            <Input
              id="meta-title-input"
              value={metaTitleDraft}
              onChange={(event) => setMetaTitleDraft(event.target.value)}
              placeholder="Ej: Nuestra Boda - Ana & Juan"
              className="h-12 rounded-2xl border-indigo-100 bg-white/90"
            />
            {isTitleDirty ? (
              <MetadataActions
                saving={savingTextKey === 'meta_title'}
                onCancel={() => setMetaTitleDraft(savedMetaTitle)}
                onSave={() => { void saveTextField('meta_title', metaTitleDraft); }}
              />
            ) : null}
          </div>

          <div className="space-y-2">
            <label htmlFor="meta-description-input" className="text-sm font-semibold text-slate-700">
              Descripción
            </label>
            <Textarea
              id="meta-description-input"
              value={metaDescriptionDraft}
              onChange={(event) => setMetaDescriptionDraft(event.target.value)}
              placeholder="Describe brevemente de qué trata el evento..."
              className="min-h-[60px] rounded-2xl border-indigo-100 bg-white/90"
            />
            {isDescriptionDirty ? (
              <MetadataActions
                saving={savingTextKey === 'meta_description'}
                onCancel={() => setMetaDescriptionDraft(savedMetaDescription)}
                onSave={() => { void saveTextField('meta_description', metaDescriptionDraft); }}
              />
            ) : null}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Imagen</label>
            <div className="rounded-[28px] border border-dashed border-indigo-100 bg-white/80 p-5 shadow-inner">
              <div className="flex min-h-[280px] flex-col items-center justify-center gap-4 rounded-[22px] bg-slate-50/70 px-6 py-8 text-center">
                {activeMetaImagePreview ? (
                  <img
                    src={activeMetaImagePreview}
                    alt="Vista previa de la imagen OG"
                    className="h-auto max-w-full rounded-2xl border border-slate-200 shadow-sm"
                  />
                ) : (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">{ogImagePreviewLabel}</p>
                    <p className="text-sm text-slate-400">Recomendado: 1200 x 630 px para mejor visualización en redes sociales.</p>
                  </div>
                )}

                {activeMetaImagePreview ? (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">{ogImagePreviewLabel}</p>
                    <p className="text-sm text-slate-400">Recomendado: 1200 x 630 px para mejor visualización en redes sociales.</p>
                  </div>
                ) : null}

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button type="button" variant="outline" onClick={() => ogImageInputRef.current?.click()}>
                    <Upload className="mr-2 h-4 w-4" />
                    Seleccionar imagen
                  </Button>
                  {selectedMetaImageFile ? (
                    <Button type="button" onClick={() => { void uploadMetaImage(); }} disabled={uploadingMetaImage}>
                      {uploadingMetaImage ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Subiendo…
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Subir imagen
                        </>
                      )}
                    </Button>
                  ) : null}
                </div>

                {selectedMetaImageFile ? (
                  <Button type="button" variant="ghost" onClick={clearSelectedMetaImage} className="text-slate-500">
                    <X className="mr-2 h-4 w-4" />
                    Cancelar selección
                  </Button>
                ) : null}
              </div>

              <input
                ref={ogImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  handleMetaImageSelect(event.target.files?.[0] || null);
                  event.currentTarget.value = '';
                }}
              />
            </div>
            <p className="text-sm text-slate-500">
              Los cambios pueden tomar hasta 24hs en reflejarse.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ShareTab;
